import { forEach } from "jszip";
import { nextFrame } from "../../../library-js/animation/nextFrame";
import { DOMHitTest } from "../../../library-js/dom-tools/DOMHitTest";
import { ElementType } from "../../../library-js/dom-tools/ElementType";
import { Rokojori } from "../../js/Rokojori";
import { Slide } from "./Slide";
import { ClassFlag } from "../../../library-js/dom-tools/ClassFlag";
import { OnClick, OnDoubleClick, OnMouseEnter, OnMouseLeave, OnSwipe, OnSwipeHorizontal } from "../../../library-js/dom-tools/EventListeners";
import { Fullscreen } from "../../../library-js/dom-actions/Fullscreen";
import { MathX } from "../../../library-js/math/MathX";
import { HTMLEditor } from "../../../library-js/dom-tools/HTMLEditor";
import { InputManager } from "../../../library-js/dom-tools/InputManager";
import { ActivityAnalyser } from "../../../library-js/tools/ActivityAnalyser";
import { ElementAttribute } from "../../../library-js/dom-tools/ElementAttribute";
import { sleep } from "../../../library-js/animation/sleep";
import { OnAnimationFrame } from "../../../library-js/animation/OnAnimationFrame";
import { VideoPlayer } from "../../../library-templates/video--player/VideoPlayer";
import { App } from "../../../library-js/app/App";


export class PresentationElement extends HTMLElement
{
  _active:number = 0;
  _slides:HTMLElement[];
  _presenting:boolean = false;
  _index:number= 0;
  _currentWidth:string = null;

  
}

export class Presentation
{
  static readonly type = new ElementType( "presentation-element" );

  static readonly slideSelector = new ElementType( "presentation-slide-selector" );
  static readonly selectorSlideEntry = new ElementType( "selector-slide-entry" );
  static readonly chapter = new ClassFlag( "chapter" );

  static readonly startPresentationElement = new ElementType( "start-presentation" );
  static readonly endPresentationElement = new ElementType( "end-presentation" );
  static readonly fullscreenToggle = new ElementType( "fullscreen-toggle" );

  static readonly previousSlide = new ElementType( "previous-slide" );
  static readonly nextSlide = new ElementType( "next-slide" );

  static readonly activeSlide = new ElementType( "active-slide" );
  static readonly allSlides = new ElementType( "all-slides" );

  static readonly showNotesElement = new ElementType( "show-notes" );

  static readonly slideElement = new ElementType( "slide-element" );
  static readonly slideContent = new ElementType( "slide-content" );
  static readonly slideInfo = new ElementType( "slide-info" );

  static readonly presentingFlag = new ClassFlag( "presenting" );
  static readonly hoverSlideLabel = new ElementType( "hover-slide-label" );

  static readonly mediaShowCase = new ElementType( "media-show-case" );
  static readonly mediaSelect = new ElementType( "media-select" );
  static readonly mediaContainer = new ElementType( "media-container" );

  static presentations:PresentationElement[] = [];
  static activePresentation:number = -1;
  static cleanUps = [];

  static applyOnDocument( inputManager:InputManager, onFrame:OnAnimationFrame )
  { 
    this.cleanUp();
    
    Presentation.presentations = [];
    Presentation.activePresentation = -1;
    Presentation.type.forAllInDoc( e=> Presentation.applyOnElement( e, inputManager, onFrame ) );
  }

  static cleanUp()
  {
    Presentation.cleanUps.forEach( c => c() );
    Presentation.cleanUps = [];
  }

  static async startPresentation( pe:PresentationElement ):Promise<void>
  {
    pe._presenting = true;

    Presentation.presentingFlag.set( pe );

    // App.logInfo( "Making fullscreen", pe._active, pe );
    await Fullscreen.toggle( pe );

    Presentation.loadActiveSlide( pe );     

    Presentation.activePresentation = pe._index;
    
    return Promise.resolve();
  }

  
  static endPresentation( pe:PresentationElement )
  {
    pe._presenting = false;

    Presentation.activePresentation = -1;

    Presentation.presentingFlag.removeFrom( pe );

    if ( Fullscreen.active )
    {
      Fullscreen.toggle( pe );
    }    

  }

  static changeSlide( pe:PresentationElement, direction:number )
  {
    let next = pe._active + direction;
    Presentation.setActiveSlide( pe, next );
  }

  static setActiveSlide( pe:PresentationElement, index:number )
  {
    pe._active = MathX.clamp( index, 0, pe._slides.length - 1 );

    Presentation.activeSlide.forAll( pe, e => HTMLEditor.setText( e, ( pe._active + 1 ) + "" ) );
    
    Presentation.loadActiveSlide( pe );
  }

  static async loadActiveSlide( pe:PresentationElement )
  {
    pe._slides.forEach(
      slide => 
      {
        ClassFlag.active.removeFrom( slide );
        ClassFlag.visible.removeFrom( slide );
      }
    );

    
    ClassFlag.active.set( pe._slides[ pe._active ] );
    
    await sleep( 100 );

    ClassFlag.visible.set( pe._slides[ pe._active ] );

    // App.logInfo( "Active slide:", pe._active, ">>", pe._slides[ pe._active ] );
  }

  static appliedKeys = false;

  static applyKeys( inputManager:InputManager )
  {
    if ( Presentation.appliedKeys )
    {
      return;
    }
    

    inputManager.onKey.addListener(
      ( keyEvent )=>
      {
        if ( Presentation.activePresentation == -1 )
        {
          return;
        }
        
        var pe = this.presentations[ this.activePresentation ];

        var key = keyEvent.key;

        if ( key === "F" ||key === "f" )
        {
          Fullscreen.toggle( pe );
          return;
        }


        var direction = 0;

        if ( key === "A" ||key === "a" || key === "ArrowLeft" || key == "ArrowUp" )
        {
          direction = -1;
        }
        else if ( key === "D" ||key === "d" || key === "ArrowRight" || key == "ArrowDown" )
        {
          direction = 1;
        }

        if ( direction == 0 )
        {
          return;
        }

        
        Presentation.changeSlide( pe, direction );
      }
    );
    


  }

  static applyOnElement( e:Element, inputManager:InputManager, onFrame:OnAnimationFrame )
  { 
    Presentation.applyKeys( inputManager );

    let buttons = ElementType.button.queryAll( e );

    let toggleSlides = new ClassFlag( "toggle-slides" );
    let hideSlides = new ClassFlag( "hide-slides" );

    buttons.forEach(
      ( b )=>
      {
        if ( ! toggleSlides.in( b ) )
        {
          return;
        }

        OnClick.add( b, ()=>
          {
            hideSlides.toggle( e );
          }
        );
      }
    )


    let pe = e as PresentationElement;
    pe._slides = Presentation.slideElement.queryAll( e ) as HTMLElement[];
    pe._active = 0;
    pe._index = this.presentations.length;

    
    let updater = ()=>
    {
      let nextWidth = "";

      if ( Fullscreen.active )
      {
        nextWidth = "calc( var(--sw) )";
        
      }
      else
      {
        nextWidth  = "calc( ( 60 * var(--sw) + 200px ) / 100.0 )";
      }

      if ( pe._currentWidth == nextWidth )
      {
        return;
      }

      pe._currentWidth = nextWidth;
      pe.setAttribute( "style", `--pw:${nextWidth}` );

    };

    onFrame.addListener( updater );

    this.cleanUps.push( ()=>{ onFrame.removeListener( updater ) } );


    Presentation.mediaShowCase.forAll( e, 
      ( mc )=>
      {
        Presentation.mediaSelect.forAllOnClick( mc,
          ms =>
          { 
            let allSelectors = Presentation.mediaSelect.queryAll( mc );
            allSelectors.forEach(
              ( s )=>
              {
                ClassFlag.active.setAs( s, s == ms );
              }
            );

            let id = ElementAttribute.dataID.from( ms );

            let containers = Presentation.mediaContainer.queryAll( mc );
            
            containers.forEach(
              ( c )=>
              {
                let isActive = ElementAttribute.dataID.from( c ) == id;
                ClassFlag.selected.setAs( c, isActive );

                if ( ! isActive )
                {
                  let videoPlayer = VideoPlayer.type.query( c );
                  VideoPlayer.pauseVideo( videoPlayer );
                }
              }
            )
          }
        );
      }
    );
    let slideSelector = Presentation.slideSelector.query( e );

    OnSwipeHorizontal.add( pe, 
      ( type )=> 
      {
        let direction = type === "Left" ? 1 : -1;
        Presentation.changeSlide( pe, direction );     
      }
    );

    let hoverSlideLabel = Presentation.hoverSlideLabel.query( e );

    let chapter:HTMLElement = null;

    pe._slides.forEach(
      ( slide )=>
      {
        let index = pe._slides.indexOf( slide ) + 1;
        let title = ElementAttribute.title.from( slide );
        //console.log( slide );
        let isChapter = title.startsWith( "[" );

        if ( isChapter )
        {
          let chapterEnd = title.indexOf( "]");
          let chapterName = title.substring( 1, chapterEnd );

          let chapterLabel = new ElementType<HTMLElement>( "slides-chapter-label").create( chapterName )
          chapter = new ElementType<HTMLElement>( "slides-chapter" ).create( chapterLabel );
          chapter.appendChild( ElementType.break.create() );


          slideSelector.appendChild( chapter );

          title = title.substring( chapterEnd + 1 );
        }
        
        let entry = Presentation.selectorSlideEntry.create( index + "") as HTMLElement;

        Presentation.chapter.setAs( entry, isChapter );
        // entry.style.width = `calc( 80.0 * var(--sw) / ${pe._slides.length} )`; 
        // entry.style.marginRight = `calc( 3.0 * var(--sw) / ${pe._slides.length} )`; 
        chapter.appendChild( entry );

        let info = Presentation.slideInfo.query( slide );

        info.prepend( ElementType.h1.create( title ) );
        OnMouseEnter.add( entry, ()=>
          { 
            HTMLEditor.setText( hoverSlideLabel, title );
          } 
        );

        OnMouseLeave.add( entry, ()=>
          { 
            HTMLEditor.setText( hoverSlideLabel, "" );
          } 
        );

        OnClick.add( entry, ()=>
          {
            Presentation.setActiveSlide( pe, index - 1 );
          } 
        );
      }
    )

    this.presentations.push( pe );

    Presentation.allSlides.forAll( e, e => HTMLEditor.setText( e, pe._slides.length + "" ) );
    Presentation.activeSlide.forAll( e, e => HTMLEditor.setText( e, ( pe._active + 1 ) + "" ) );

    pe._slides.forEach(
      ( slide )=>
      {
        OnDoubleClick.add( slide,
          async ev => 
           {
            try
            {
              

              if ( pe._presenting )
              {
                // App.logInfo( "Toggle FS via Slide, already presenting" );
                await Fullscreen.toggle( pe );
              }
              else
              {
                // App.logInfo( "Toggle FS via Slide, not presenting" );
                pe._active = pe._slides.indexOf( slide );
                Presentation.activeSlide.forAll( pe, e => HTMLEditor.setText( e, ( pe._active + 1 ) + "" ) );
                await Presentation.startPresentation( pe );
              }

              await nextFrame();

              let selection = window.getSelection();

              if ( selection ) 
              {
                selection.removeAllRanges();
              }

            }
            catch ( exc )
            {
              App.logError( exc, exc.trace );
            }

          }
        );
      }
    );

    Presentation.showNotesElement.forAllOnClick( e,
      ( el, ev )=>
      {
        let flag = new ClassFlag( "show-infos" );
        flag.toggle( pe );
      } 
    );

    Presentation.previousSlide.forAllOnClick( e,
      ( el, ev )=>
      {
        Presentation.changeSlide( pe, -1 );
      } 
    );

    Presentation.nextSlide.forAllOnClick( e,
      ( el, ev )=>
      {
        Presentation.changeSlide( pe, 1 );
      }
    );


    Presentation.startPresentationElement.forAllOnClick( e,
      ( el, ev )=>
      {
        Presentation.startPresentation( pe );
      }
    );

    Presentation.endPresentationElement.forAllOnClick( e,
      ( el, ev )=>
      {
        Presentation.endPresentation( pe );
      }
    );

    Presentation.fullscreenToggle.forAllOnClick( e,
      ( el, ev )=>
      {
        if ( Fullscreen.active )
        {
          Fullscreen.toggle( pe );
        }
        else
        {
          Presentation.startPresentation( pe );
        }
          
      }
    );
    
  } 

    
}
